import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { toast } from "react-toastify";

import api from "@/utils/axios";
import cls from "./UserMemberCreate.module.scss";

const UserMemberCreate = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    username: "",
    email: "",
    birthday: "",
    password: "",
    confirmPassword: "",
  });

  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleBack = () => {
    navigate("/admin/members");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.username || !form.email || !form.birthday || !form.password) {
      toast.error("Please fill all required fields.");
      return;
    }

    if (form.password !== form.confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    try {
      setSubmitting(true);

      await api.post("/v1/admin/members/create", {
        username: form.username.trim(),
        email: form.email.trim(),
        birthday: form.birthday,
        password: form.password,
      });

      toast.success("Member created successfully.");
      navigate("/admin/members");
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.message ||
        "Failed to create member. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={cls.page}>
      <div className={cls.headerRow}>
        <button
          type="button"
          className={cls.backBtn}
          onClick={handleBack}
          disabled={submitting}
        >
          <span aria-hidden="true" className={cls.backIcon}>
            ←
          </span>
          <span>Back to members</span>
        </button>

        <div className={cls.headerText}>
          <h1 className={cls.title}>Create Member</h1>
          <p className={cls.subtitle}>
            Create a member account from the admin panel. The user will get an
            active member profile with default personal and lifestyle data.
          </p>
        </div>
      </div>

      <Motion.section
        className={cls.card}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        <div className={cls.cardGlow} />
        <div className={cls.cardInner}>
          <div className={cls.cardHeader}>
            <h2 className={cls.cardTitle}>Account details</h2>
            <p className={cls.cardHint}>
              Email will be marked as verified. Role will be set to{" "}
              <strong>member</strong> and status to <strong>active</strong>. A
              linked member profile will be created with{" "}
              <strong>origin: bot</strong> and <strong>status: pending</strong>.
            </p>
          </div>

          <form className={cls.form} onSubmit={handleSubmit} noValidate>
            <div className={cls.grid}>
              {/* Username */}
              <div className={cls.field}>
                <label htmlFor="username" className={cls.label}>
                  Username <span className={cls.required}>*</span>
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  className={cls.input}
                  value={form.username}
                  onChange={handleChange}
                  autoComplete="off"
                  placeholder="cm_user_01"
                  disabled={submitting}
                />
                <p className={cls.help}>
                  This name shows inside CMates. You can use letters, numbers,
                  and underscores.
                </p>
              </div>

              {/* Email */}
              <div className={cls.field}>
                <label htmlFor="email" className={cls.label}>
                  Email <span className={cls.required}>*</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  className={cls.input}
                  value={form.email}
                  onChange={handleChange}
                  autoComplete="off"
                  placeholder="user@example.com"
                  disabled={submitting}
                />
                <p className={cls.help}>
                  Used for login and notifications. It will be marked as
                  verified.
                </p>
              </div>

              {/* Birthday */}
              <div className={cls.field}>
                <label htmlFor="birthday" className={cls.label}>
                  Birthday <span className={cls.required}>*</span>
                </label>
                <input
                  id="birthday"
                  name="birthday"
                  type="date"
                  className={cls.input}
                  value={form.birthday}
                  onChange={handleChange}
                  disabled={submitting}
                />
                <p className={cls.help}>
                  Used only for age validation and internal stats.
                </p>
              </div>

              {/* Password */}
              <div className={cls.field}>
                <label htmlFor="password" className={cls.label}>
                  Password <span className={cls.required}>*</span>
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  className={cls.input}
                  value={form.password}
                  onChange={handleChange}
                  autoComplete="new-password"
                  placeholder="Enter a strong password"
                  disabled={submitting}
                />
                <p className={cls.help}>
                  Use at least 8 characters, with a mix of letters, numbers, and
                  symbols.
                </p>
              </div>

              {/* Confirm Password */}
              <div className={cls.field}>
                <label htmlFor="confirmPassword" className={cls.label}>
                  Confirm password <span className={cls.required}>*</span>
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  className={cls.input}
                  value={form.confirmPassword}
                  onChange={handleChange}
                  autoComplete="new-password"
                  placeholder="Repeat the password"
                  disabled={submitting}
                />
                <p className={cls.help}>
                  This must match the password you entered above.
                </p>
              </div>
            </div>

            <div className={cls.footerRow}>
              <div className={cls.meta}>
                <div className={cls.metaItem}>
                  <span className={cls.metaLabel}>User role</span>
                  <span className={cls.metaValue}>member</span>
                </div>
                <div className={cls.metaItem}>
                  <span className={cls.metaLabel}>User status</span>
                  <span className={cls.metaValue}>active</span>
                </div>
                <div className={cls.metaItem}>
                  <span className={cls.metaLabel}>Member status</span>
                  <span className={cls.metaValuePending}>pending</span>
                </div>
              </div>

              <div className={cls.actions}>
                <button
                  type="button"
                  className={cls.cancelBtn}
                  onClick={handleBack}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={cls.submitBtn}
                  disabled={submitting}
                >
                  {submitting ? "Creating..." : "Create member"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </Motion.section>
    </div>
  );
};

export default UserMemberCreate;
